import React, { useEffect, useMemo } from 'react';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import PageLayout from 'layouts/PageLayout';
import TableFilterToolbar from 'components/TableFilterToolbar';
import PropTypes from 'prop-types';
import withTableProps from 'HOC/types/withTable';
import withTable from 'HOC/withTable';
import { transformTableData } from 'utils';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ROLES } from 'appConstants';
import { useHistory } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { Button, Icon } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useStudentMoodleSync } from 'hooks/useStudentMoodleSync';
import {
  downloadTrainingSessionCertificate,
  getTrainingSessionsList,
  setTrainingSessionRating,
} from 'http/trainingSessions';
import { routesAliases } from 'router/routesAliases';
import { actions as modalActions } from 'redux/modal';
import { openLtiTrainingSession } from 'utils/openLtiTrainingSession';
import { handleCertificateDownload } from 'utils/certificateDownloadHandler';
import getTableConfig from 'pages/TrainingSessions/helpers/getTableConfig';
import getStudentTableConfig, {
  mapParticipantStatusForRating,
  mapParticipantStatusToResult,
} from './helpers/getStudentTableConfig';
import FeedbackForm from './FeedbackForm';

const TRAINING_SESSION_STATUSES = ['draft', 'scheduled', 'active', 'completed', 'cancelled'];
const TRAINER_SESSION_STATUSES = ['scheduled', 'active', 'completed', 'cancelled'];
const STUDENT_RESULT_STATUSES = ['Pending', 'Passed', 'Failed'];

const pageAlias = 'MyTrainingSessionsList';

const useStyles = makeStyles(() => ({
  modal: {
    '& .MuiDialog-paper': {
      width: '350px',
    },
  },
  modalCertificate: {
    textAlign: 'center',
    '& .MuiDialog-paper': {
      width: '500px',
    },
  },
  wrapper: {
    textAlign: 'center',
  },
  btn: {
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:first-child': {
      marginRight: 20,
    },
    '&:last-child': {
      marginLeft: 20,
    },
  },
  btnWrapper: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const MyTrainingSessions = ({ tableData }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation('common');
  const profileId = useSelector(state => state.auth.profile?.id);
  const role = useSelector(state => state.auth.profile?.role);
  const isStudent = role === ROLES.USER;
  const isTrainer = role === ROLES.TRAINER;

  const handleCloseModal = () => {
    dispatch(modalActions.closeModal());
    tableData.handleForceUpdate();
  };

  const [studentRatingState, setStudentRatingState] = useAsyncFn(async (trainingSessionId, rating) => {
    const data = await setTrainingSessionRating(trainingSessionId, rating);
    if (!data.error) {
      handleCloseModal();
    }
  });

  const [downloadCertificateState, downloadCertificate] = useAsyncFn(
    async (trainingSessionId, userId) =>
      downloadTrainingSessionCertificate(trainingSessionId, userId),
  );

  const handleRatingModal = (key, rating) => {
    dispatch(
      modalActions.openModal(
        {
          size: 'large',
          title: t('courses.feedback'),
          customStyles: classes.modal,
        },
        <FeedbackForm
          trainingSessionId={key}
          rating={rating}
          onSubmit={setStudentRatingState}
          onCancel={handleCloseModal}
        />,
      ),
    );
  };

  const handleShowCertificate = async (trainingSessionId, certificate) => {
    if (
      await handleCertificateDownload({
        certificate,
        courseId: trainingSessionId,
        userId: profileId,
        downloadCertificate,
        dispatch,
        t,
      })
    ) {
      return;
    }

    const certificateUrl = certificate && certificate.previewUrl;
    const picture = certificateUrl ? (
      <img src={certificateUrl} width="200" height="300" alt="" />
    ) : (
      <Icon color="primary" style={{ fontSize: 150 }}>
        image
      </Icon>
    );

    dispatch(
      modalActions.openModal(
        {
          size: 'large',
          title: t('trainingDetails.certificateModalTitle'),
          customStyles: classes.modalCertificate,
        },
        <div className={classes.wrapper}>
          {picture}
          <div className={classes.btnWrapper}>
            <Button
              className={classes.btn}
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                downloadCertificate(trainingSessionId, profileId);
                handleCloseModal();
              }}
            >
              {t('buttons.save')}
            </Button>
            <Button className={classes.btn} variant="outlined" size="large" onClick={handleCloseModal}>
              {t('buttons.cancel')}
            </Button>
          </div>
        </div>,
      ),
    );
  };

  const tableConfig = useMemo(
    () =>
      isStudent
        ? getStudentTableConfig({ handleRatingModal, handleShowCertificate })
        : getTableConfig({ forTrainer: isTrainer }),
    [isStudent, isTrainer],
  );

  const tableDataExtended = useMemo(() => {
    if (!Array.isArray(tableData.data)) {
      return tableData.data;
    }
    if (!isStudent) {
      return tableData.data;
    }
    return tableData.data.map(item => ({
      ...item,
      resultStatus: mapParticipantStatusToResult(item.participantStatus),
      feedback: {
        status: mapParticipantStatusForRating(item.participantStatus),
        rating: item.rating || { sessionQuality: null, courseQuality: null, trainersQualityArray: [] },
      },
      certificate: item.certificatePreviewUrl
        ? { previewUrl: item.certificatePreviewUrl }
        : item.hasCertificate
        ? {}
        : null,
    }));
  }, [tableData.data, isStudent]);

  const [columns, items] = transformTableData(tableConfig, tableDataExtended);

  useStudentMoodleSync(() => {
    tableData.handleForceUpdate();
  });

  useEffect(() => {
    if (!profileId) {
      return;
    }
    tableData.handleChangeFetchMethod(pageAlias, (params = {}) => {
      if (role === ROLES.TRAINER) {
        return getTrainingSessionsList({ ...params, trainerUserId: profileId });
      }
      return getTrainingSessionsList(params);
    });
  }, [profileId, role]);

  const statusFilterOptions = useMemo(() => {
    if (isStudent) {
      return STUDENT_RESULT_STATUSES.map(value => ({
        value,
        label: value,
      }));
    }
    const statuses = isTrainer ? TRAINER_SESSION_STATUSES : TRAINING_SESSION_STATUSES;
    return statuses.map(value => ({
      value,
      label: t(`trainingSessions.statuses.${value}`),
    }));
  }, [isStudent, isTrainer, t]);

  const filters = useMemo(
    () => [
      {
        type: 'search',
        name: 'search',
        label: t('trainingSessions.searchLabel'),
        placeholder: t('trainingSessions.searchPlaceholder'),
      },
      {
        type: 'singleSelect',
        name: isStudent ? 'resultStatus' : 'status',
        label: t('tablesLabels.status'),
        options: statusFilterOptions,
      },
    ],
    [isStudent, statusFilterOptions, t],
  );

  const isLoading =
    tableData.isFetchingTableData || studentRatingState.loading || downloadCertificateState.loading;

  return (
    <PageLayout
      pageTitle={t('trainingSessions.myTrainingSessions')}
    >
      <TableFilterToolbar
        filters={filters}
        filtering={tableData.filtering}
        onFilterChange={(name, value) => {
          if ((name === 'status' || name === 'resultStatus') && !value) {
            tableData.handleRemoveFilter(name);
            return;
          }
          tableData.handleFiltering(name, value);
        }}
        onRemoveFilter={name => {
          tableData.handleRemoveFilter(name);
        }}
        onClearFilters={tableData.handleClearFilters}
      />
      <ModalBusyContainer busy={isLoading}>
        <AppTable
          columns={columns}
          data={items}
          totalCount={tableData.totalCount}
          rowsPerPage={tableData.pageSize}
          onChangePage={tableData.handleChangePage}
          onColumnSorting={tableData.handleSorting}
          currentPage={tableData.page}
          isLazyLoading
          actions={[
            {
              title: t('buttons.info'),
              onClick: event => {
                const { key } = event.currentTarget.dataset;
                history.push(`${routesAliases.viewMyTrainingSession}/${key}`);
              },
            },
            ...(isTrainer
              ? []
              : [
                  {
                    title: t('buttons.open'),
                    onClick: event => {
                      const { key } = event.currentTarget.dataset;
                      openLtiTrainingSession({
                        trainingSessionId: Number(key),
                        t,
                        dispatch,
                        modalActions,
                      });
                    },
                    disabled: rowData => !rowData?.hasMoodleLink,
                    getTitle: rowData => {
                      if (rowData?.hasMoodleLink) {
                        return t('buttons.open');
                      }
                      const state = rowData?.moodleAccessState;
                      if (state) {
                        const message = t(`trainingSessions.moodleAccessBlocked.${state}`, {
                          defaultValue: '',
                        });
                        if (message) {
                          return message;
                        }
                      }
                      return t('buttons.open');
                    },
                  },
                ]),
          ]}
          extraActionsKeys={['hasMoodleLink', 'moodleAccessState']}
        />
      </ModalBusyContainer>
    </PageLayout>
  );
};

MyTrainingSessions.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
};

export default withTable({
  defaultSorting: { field: 'id', dir: 'desc' },
  allowedFilters: ['search', 'status', 'resultStatus'],
  fetchingData: 'items',
  sortingFieldsMapping: {
    status: 'statusSort',
  },
  pageAlias,
})(MyTrainingSessions);
